import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Topbar } from "./Topbar";
import { Sidebar } from "./Sidebar";
import { TierBanner } from "./TierBanner";
import { BottomNav } from "./BottomNav";
import { IncidentPanel } from "./IncidentPanel";
import { NewIncidentModal } from "./NewIncidentModal";
import { ToastViewport } from "./ToastSystem";
import { FmeaPanel } from "./FmeaPanel";
import { IntegrationModal } from "./IntegrationModal";
import { EightDPanel } from "./EightDPanel";
import { LPAPanel } from "./LPAPanel";
import { NewEightDModal, NewLPAModal } from "./NewModuleModals";
import { useStore } from "../store";
import { useWebSocket } from "../api/useWebSocket";
import { SearchPalette } from "./SearchPalette";
import { CopilotPanel } from "./CopilotPanel";
import { useSearchPalette } from "../hooks/useSearchPalette";
import { trackModuleView } from "../lib/activityTracker";

export const Layout = () => {
  // Single WS instance for the authenticated session
  const ws = useWebSocket();
  const isPanelOpen = useStore((s) => s.isPanelOpen);
  const isFmeaPanelOpen = useStore((s) => s.isFmeaPanelOpen);
  const isModalOpen = useStore((s) => s.isModalOpen);
  const closePanel = useStore((s) => s.closePanel);
  const closeFmeaPanel = useStore((s) => s.closeFmeaPanel);
  const closeModal = useStore((s) => s.closeModal);
  const openModal = useStore((s) => s.openModal);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const addToast = useStore((s) => s.addToast);
  const palette = useSearchPalette();
  const location = useLocation();

  // Iter82 — emit a module_view on every route change (fire-and-forget).
  useEffect(() => {
    trackModuleView(location.pathname);
  }, [location.pathname]);

  // Iter33: removed timed briefing modal. Critical state surfaces in the
  // dashboard's Risk Index card and the Copilot's situational chips instead.

  // Set topbar height var responsively
  useEffect(() => {
    const apply = () => {
      const h = window.innerWidth < 640 ? 50 : 56;
      document.documentElement.style.setProperty("--topbar-h", `${h}px`);
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  // Auto-fire toasts on initial load
  useEffect(() => {
    const t1 = setTimeout(() => {
      addToast({
        type: "s1",
        title: "SLA Warning — INC-2024-0888",
        body: "Leoni AG · Resolution at 78% of threshold",
      });
    }, 2500);
    const t2 = setTimeout(() => {
      addToast({
        type: "info",
        title: "Comment received",
        body: "DB Schenker updated INC-2024-0875",
      });
    }, 6500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
     
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      // ⌘K / Ctrl+K — global palette
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        if (palette.isOpen) palette.close();
        else palette.open();
        return;
      }
      if (e.key === "Escape") {
        if (palette.isOpen) palette.close();
        else if (isModalOpen) closeModal();
        else if (isFmeaPanelOpen) closeFmeaPanel();
        else if (isPanelOpen) closePanel();
        else setSidebarOpen(false);
      } else if (e.key === "n" || e.key === "N") {
        const tag = (document.activeElement && document.activeElement.tagName) || "";
        if (!["INPUT", "TEXTAREA", "SELECT"].includes(tag)) {
          if (!isModalOpen && !palette.isOpen) {
            e.preventDefault();
            openModal();
          }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, isPanelOpen, isFmeaPanelOpen, openModal, closeModal, closePanel, closeFmeaPanel, setSidebarOpen, palette]);

  return (
    <div className="App min-h-screen" data-testid="app-root">
      <Topbar />
      <Sidebar />
      <main
        className="relative"
        style={{
          paddingTop: "var(--topbar-h, 56px)",
          paddingLeft: 0,
          paddingBottom: 0,
        }}
        data-testid="main-content-area"
      >
        <div
          className="lg:pl-[236px] pb-[80px] sm:pb-6"
          style={{ minHeight: "calc(100vh - var(--topbar-h, 56px))", width: "100%" }}
        >
          <div className="view-enter p-4 sm:p-6 lg:p-7 w-full">
            <TierBanner />
            <Outlet />
          </div>
        </div>
      </main>
      <BottomNav />
      <IncidentPanel />
      <FmeaPanel />
      <EightDPanel />
      <LPAPanel />
      <NewIncidentModal />
      <NewEightDModal />
      <NewLPAModal />
      <IntegrationModal />
      <ToastViewport />
      <SearchPalette />
      <CopilotPanel />
    </div>
  );
};
